import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UtensilsCrossed, Bell, ShoppingBasket, Star } from 'lucide-react';
import DinnerMagic from './DinnerMagic';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = [['mon', 'Mån'], ['tue', 'Tis'], ['wed', 'Ons'], ['thu', 'Tor'], ['fri', 'Fre'], ['sat', 'Lör'], ['sun', 'Sön']];

const dateOf = (monday, i) => {
  const d = new Date(`${monday}T12:00:00`);
  d.setDate(d.getDate() + i);
  return d.getDate();
};

function FavoriteChips({ monday, dinners, onPick }) {
  const [favs, setFavs] = useState([]);
  useEffect(() => {
    axios.get(`${API}/mysida/dinner-favorites`, { params: { date: monday }, withCredentials: true })
      .then((r) => setFavs(r.data.favorites || [])).catch(() => {});
  }, [monday]);
  const planned = new Set(Object.values(dinners || {}).map((d) => (d || '').trim().toLowerCase()));
  const shown = favs.filter((f) => !planned.has(f.dish.toLowerCase()));
  if (!shown.length) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="dinner-favorites">
      <span className="flex items-center gap-1 text-[11px] font-black uppercase text-[#B5672A]"><Star className="w-3 h-3" fill="currentColor" />Favoriter</span>
      {shown.map((f, i) => (
        <button key={f.dish} onClick={() => onPick(f.dish)} data-testid={`dinner-fav-${i}`}
          className="px-2.5 py-1 rounded-full text-xs font-bold bg-white border border-[#F2C9A0] text-[#8A4B1E] transition-transform hover:scale-105 hover:border-[#D9822B]">
          {f.dish}{f.count > 1 && <span className="opacity-50"> ×{f.count}</span>}
        </button>
      ))}
    </div>
  );
}

export default function WeekDinners({ monday, data, setDinner, setRemind, setShopDays, setDinners }) {
  const on = !!data.dinner_remind;
  const lastFocus = useRef(null);
  const shop = data.shop_days || [];
  const pick = (dish) => {
    const empty = (k) => !(data.dinners?.[k] || '').trim();
    const k = lastFocus.current && empty(lastFocus.current) ? lastFocus.current : DAYS.map(([d]) => d).find(empty);
    if (!k) { toast('Alla dagar har redan en middag 💛'); return; }
    setDinner(k, dish);
    lastFocus.current = null;
    toast.success(`${dish} på ${DAYS.find(([d]) => d === k)[1].toLowerCase()}`);
  };
  const toggleShop = (k) => setShopDays(shop.includes(k) ? shop.filter((x) => x !== k) : [...shop, k]);
  return (
    <section className="mb-4 rounded-xl border-2 border-[#F2C9A0] bg-[#FFF6EC] p-3" data-testid="week-dinners">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2 text-[#8A4B1E]">
          <UtensilsCrossed className="w-4 h-4" />
          <span className="font-black tracking-wide">Veckans middagar</span>
          <span className="text-xs opacity-70 hidden sm:inline">· skriv en gång, Elsa påminner varje dag</span>
        </div>
        <label className="flex items-center gap-2 text-xs font-bold text-[#8A4B1E]">
          <Bell className="w-3.5 h-3.5" />
          <input type="checkbox" checked={on} onChange={(e) => setRemind(e.target.checked ? '15:00' : '')} data-testid="week-dinner-remind-toggle" />
          Notis
          {on && (
            <input type="time" value={data.dinner_remind} onChange={(e) => e.target.value && setRemind(e.target.value)}
              className="border border-[#F2C9A0] rounded px-1 py-0.5 bg-white" data-testid="week-dinner-remind-time" />
          )}
        </label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1.5">
        {DAYS.map(([k, label], i) => (
          <div key={k} className="flex items-center gap-2 bg-white rounded-lg border border-[#F2C9A0] px-2 py-1.5 transition-colors focus-within:border-[#D9822B]">
            <span className="w-12 flex-shrink-0 text-[11px] font-black uppercase text-[#B5672A]">{label} {dateOf(monday, i)}</span>
            <input value={data.dinners?.[k] || ''} onChange={(e) => setDinner(k, e.target.value)} onFocus={() => { lastFocus.current = k; }}
              placeholder="t.ex. tacos" className="flex-1 min-w-0 text-sm outline-none bg-transparent" maxLength={200} data-testid={`week-dinner-${k}`} />
            <button onClick={() => toggleShop(k)} aria-pressed={shop.includes(k)} title={shop.includes(k) ? 'Handlardag, listan syns i tidningen' : 'Handlar du den här dagen?'}
              className={`p-1 rounded-md transition-colors ${shop.includes(k) ? 'bg-[#D9822B] text-white' : 'text-[#D9B48F] hover:text-[#D9822B]'}`} data-testid={`week-shop-day-${k}`}>
              <ShoppingBasket className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <p className="mt-1.5 text-[11px] text-[#8A4B1E] opacity-70">Tryck på korgen de dagar du handlar, så kommer inköpslistan med i Elsas tidning.</p>
      <FavoriteChips monday={monday} dinners={data.dinners} onPick={pick} />
      <DinnerMagic monday={monday} dinners={data.dinners} setDinners={setDinners} />
    </section>
  );
}
